import React, { useState, useEffect } from 'react'
import { Save, X, Truck, Wrench, IndianRupee, Calendar, Clock, XCircle } from 'lucide-react'
import { useData } from '../context/DataContext'
import { handleDecimalChange } from '../utils/decimalValidation'

function ServiceForm({ service, onSubmit, onCancel, isSubmitting = false }) {
  const { state } = useData()
  const { vehicles, loading } = state



  const [formData, setFormData] = useState({
    vehicle_id: '',
    service_type: 'oil',
    amount_spent: '',
    service_date: new Date().toISOString().split('T')[0],
    vehicle_hours: '',
    labour_amount: '',
    store_name: '',
    parts_brand: '',
    description: '',
    notes: '',
    payment_status: 'paid'
  })

  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (service) {
      setFormData({
        vehicle_id: service.vehicle_id || '',
        service_type: service.service_type || 'oil',
        amount_spent: service.amount_spent !== null && service.amount_spent !== undefined ? service.amount_spent : '',
        service_date: service.service_date || new Date().toISOString().split('T')[0],
        vehicle_hours: service.vehicle_hours !== null && service.vehicle_hours !== undefined ? service.vehicle_hours : '',
        labour_amount: service.labour_amount !== null && service.labour_amount !== undefined ? service.labour_amount : '',
        store_name: service.store_name || '',
        parts_brand: service.parts_brand || '',
        description: service.description || '',
        notes: service.notes || '',
        payment_status: service.payment_status || 'paid'
      })
    }
  }, [service])

  const validateForm = () => {
    const newErrors = {}

    if (!formData.vehicle_id) {
      newErrors.vehicle_id = 'Please select a vehicle'
    }

    if (!formData.service_type) {
      newErrors.service_type = 'Please select service type'
    }

    if (formData.amount_spent === '' || parseFloat(formData.amount_spent) < 0) {
      newErrors.amount_spent = 'Please enter a valid amount spent'
    }

    if (!formData.service_date) {
      newErrors.service_date = 'Please select service date'
    }

    if (formData.vehicle_hours === '') {
      newErrors.vehicle_hours = 'Please enter vehicle hours'
    }

    if (formData.labour_amount === '' || parseFloat(formData.labour_amount) < 0) {
      newErrors.labour_amount = 'Please enter a valid labour amount'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      const submitData = {
        ...formData,
        amount_spent: parseFloat(formData.amount_spent),
        vehicle_hours: parseFloat(formData.vehicle_hours),
        labour_amount: parseFloat(formData.labour_amount)
      }

      await onSubmit(submitData)
    } catch (error) {
      console.error('Error submitting service form:', error)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const selectedVehicle = vehicles.find(v => v.id === formData.vehicle_id)

  return (
    <form onSubmit={handleSubmit} className="space-y-6">

      
      {/* Service Information Section */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Wrench className="h-5 w-5 mr-2 text-brand-red" />
          Service Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Vehicle Selection */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Vehicle *
            </label>
                         <select
               value={formData.vehicle_id}
               onChange={(e) => handleInputChange('vehicle_id', e.target.value)}
               className={`input text-sm ${errors.vehicle_id ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500 focus:border-primary-500'}`}
               disabled={loading}
             >
                             <option value="">Choose a vehicle...</option>
               {loading ? (
                 <option value="" disabled>Loading vehicles...</option>
               ) : vehicles && vehicles.length > 0 ? (
                 vehicles.map(vehicle => (
                   <option key={vehicle.id} value={vehicle.id}>
                     {vehicle.registrationNumber} - {vehicle.type}
                   </option>
                 ))
               ) : (
                 <option value="" disabled>No vehicles available</option>
               )}
            </select>
            {errors.vehicle_id && (
              <p className="text-red-600 text-xs mt-1 font-medium">{errors.vehicle_id}</p>
            )}
          </div>

          {/* Service Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Type *
            </label>
                         <select
               value={formData.service_type}
               onChange={(e) => handleInputChange('service_type', e.target.value)}
               className={`input text-gray-900 ${errors.service_type ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500 focus:border-primary-500'}`}
             >
                             <option value="oil" className="text-gray-900">Oil Service</option>
               <option value="spares" className="text-gray-900">Spares/Repairs</option>
               <option value="general" className="text-gray-900">General Service</option>
               <option value="emergency" className="text-gray-900">Emergency Repair</option>
            </select>
            {errors.service_type && (
              <p className="text-red-600 text-xs mt-1 font-medium">{errors.service_type}</p>
            )}
          </div>

          {/* Service Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Date *
            </label>
            <input
              type="date"
              value={formData.service_date}
              onChange={(e) => handleInputChange('service_date', e.target.value)}
              className={`input ${errors.service_date ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500 focus:border-primary-500'}`}
            />
            {errors.service_date && (
              <p className="text-red-600 text-xs mt-1 font-medium">{errors.service_date}</p>
            )}
          </div>
        </div>
      </div>

      {/* Financial Details Section */}
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <IndianRupee className="h-5 w-5 mr-2 text-blue-600" />
          Financial Details
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Amount Spent */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount Spent (₹) *
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={formData.amount_spent}
              onChange={(e) => handleDecimalChange(e, (value) => handleInputChange('amount_spent', value))}
              className={`input ${errors.amount_spent ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500 focus:border-primary-500'}`}
              placeholder="0.00"
            />
            {errors.amount_spent && (
              <p className="text-red-600 text-xs mt-1 font-medium">{errors.amount_spent}</p>
            )}
            <p className="text-gray-600 text-xs mt-1">
              Total amount spent on service
            </p>
          </div>

          {/* Labour Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Labour Amount (₹) *
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={formData.labour_amount}
              onChange={(e) => handleDecimalChange(e, (value) => handleInputChange('labour_amount', value))}
              className={`input ${errors.labour_amount ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500 focus:border-primary-500'}`}
              placeholder="0.00"
            />
            {errors.labour_amount && (
              <p className="text-red-600 text-xs mt-1 font-medium">{errors.labour_amount}</p>
            )}
            <p className="text-gray-600 text-xs mt-1">
              Amount given to labour
            </p>
          </div>
        </div>

        {/* Store and Brand Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {/* Store Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Store Name
            </label>
            <input
              type="text"
              value={formData.store_name}
              onChange={(e) => handleInputChange('store_name', e.target.value)}
              className="input focus:ring-primary-500 focus:border-primary-500"
              placeholder="Where parts/service was bought from"
            />
            <p className="text-gray-600 text-xs mt-1">
              Name of the store/shop where parts were purchased
            </p>
          </div>

          {/* Parts Brand */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Parts Brand
            </label>
            <input
              type="text"
              value={formData.parts_brand}
              onChange={(e) => handleInputChange('parts_brand', e.target.value)}
              className="input focus:ring-primary-500 focus:border-primary-500"
              placeholder="Brand of parts used"
            />
            <p className="text-gray-600 text-xs mt-1">
              Brand of parts/oil used in service
            </p>
          </div>

          {/* Shop Payment Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Shop Payment Status
            </label>
            <select
              value={formData.payment_status}
              onChange={(e) => handleInputChange('payment_status', e.target.value)}
              className="input focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="paid">Amount Paid to Shop</option>
              <option value="not_paid">Amount Not Paid to Shop</option>
            </select>
            <p className="text-gray-600 text-xs mt-1">
              Whether the service amount has been paid to the shop or not
            </p>
          </div>
        </div>

        {/* Total Breakdown */}
        <div className="mt-4 p-3 bg-white rounded-lg border border-blue-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">Total Spent</p>
              <p className="text-lg font-bold text-red-600">
                ₹{parseFloat(formData.amount_spent || 0).toFixed(2)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">Labour Cost</p>
              <p className="text-lg font-bold text-blue-600">
                ₹{parseFloat(formData.labour_amount || 0).toFixed(2)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">Parts Cost</p>
              <p className="text-lg font-bold text-green-600">
                ₹{(parseFloat(formData.amount_spent || 0) - parseFloat(formData.labour_amount || 0)).toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Vehicle Details Section */}
      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Clock className="h-5 w-5 mr-2 text-green-600" />
          Vehicle Details
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Vehicle Hours */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vehicle Hours *
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={formData.vehicle_hours}
              onChange={(e) => handleDecimalChange(e, (value) => handleInputChange('vehicle_hours', value))}
              className={`input ${errors.vehicle_hours ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500 focus:border-primary-500'}`}
              placeholder="0.00"
            />
            {errors.vehicle_hours && (
              <p className="text-red-600 text-xs mt-1 font-medium">{errors.vehicle_hours}</p>
            )}
            <p className="text-gray-600 text-xs mt-1">
              Current vehicle hours at service time
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Description
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="input focus:ring-primary-500 focus:border-primary-500"
              placeholder="Brief description of service"
            />
          </div>
        </div>

        {/* Notes */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            rows={3}
            className="input focus:ring-primary-500 focus:border-primary-500"
            placeholder="Any additional notes or comments..."
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-3 bg-gradient-to-r from-brand-orange to-brand-red text-white rounded-lg hover:from-orange-500 hover:to-red-600 transition-all duration-200 flex items-center font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              {service ? 'Update Service' : 'Add Service'}
            </>
          )}
        </button>
      </div>
    </form>
  )
}

export default ServiceForm
